import polyglotI18nProvider from 'ra-i18n-polyglot';
import {
    AdminContext,
    AdminUI,
    CustomRoutes,
    Resource,
    localStorageStore,
    useStore,
    StoreContextProvider,
} from 'react-admin';

import { useEffect, useMemo } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { Dashboard } from '@/pages/dashboard';
import englishMessages from '@/i18n/en';
import { Layout, Login } from '@/pages/layout';
import { themes, ThemeName } from '@/themes/themes';
import authProvider, { withAuthQueryClient } from '@/services/authService/authProvider';
import { renderCustomRoutes } from '@/router';
import { getDynamicResources } from '@/router/resources';
import { useAuthMenu } from '@/services/authService/authMenu';
import { BrowserRouter, Navigate, Route } from 'react-router-dom';
import userStore from '@/store/admin/userStore';
import { prefetchAuthMenu, clearAuthMenuCache } from '@/services/authService/authMenu';
import ForcePasswordChangeDialog from '@/pages/auth/ForcePasswordChangeDialog';
import SessionExpiredDialog from '@/pages/auth/SessionExpiredDialog';
import ContainerMemoryWarningDialog from '@/pages/auth/ContainerMemoryWarningDialog';
import { getRealmKey } from '@/router/attactComponent';

const i18nProvider = polyglotI18nProvider(
    locale => {
        if (locale === 'fr') {
            return import('./i18n/fr').then(messages => messages.default);
        }

        // Always fallback on english
        return englishMessages;
    },
    'en',
    [
        { locale: 'en', name: 'English' },
        { locale: 'fr', name: 'Français' },
    ]
);

const store = localStorageStore(undefined, 'ECommerce');

// 在 Admin 内部运行的引导组件，用于预取/清空菜单缓存
const PrefetchAuthMenuInsideAdmin = () => {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (userStore.user) {
            prefetchAuthMenu(queryClient);
        } else {
            clearAuthMenuCache(queryClient);
        }
    }, [queryClient, userStore.user]);

    return null;
};

// AdminContext child: dynamic resources + short-path redirects for old bookmarks
const DynamicAdminUI = observer(() => {
    const { data: menuTree = [] } = useAuthMenu();
    const resources = getDynamicResources(menuTree);

    // Legacy short URLs (/user) → full realm_url paths (/admin/sys-manage/user)
    const shortRedirects = useMemo(() => {
        const usedLast = new Set<string>();
        const out: { short: string; full: string }[] = [];
        resources.forEach((r) => {
            const name = String(r.name || '');
            if (!name.includes('/')) return;
            const last = getRealmKey(name);
            if (!last || usedLast.has(last)) return;
            usedLast.add(last);
            out.push({ short: `/${last}`, full: `/${name}` });
        });
        // Objectives renamed to Work Done
        out.push(
            { short: '/objectives', full: '/admin/dic-manage/work-done' },
            { short: '/admin/dic-manage/objectives', full: '/admin/dic-manage/work-done' },
        );
        return out;
    }, [resources]);

    return (
        <AdminUI
            dashboard={Dashboard}
            layout={Layout}
            title="Accounting System"
            loginPage={Login}
            requireAuth
            disableTelemetry
        >
            <PrefetchAuthMenuInsideAdmin />
            <CustomRoutes>
                {renderCustomRoutes()}
                {shortRedirects.map((r) => (
                    <Route
                        key={`redir-${r.short}`}
                        path={r.short}
                        element={<Navigate to={r.full} replace />}
                    />
                ))}
            </CustomRoutes>
            {resources.map(resource => (
                <Resource
                    key={String(resource.name)}
                    name={resource.name}
                    list={resource.list}
                    create={resource.create}
                    edit={resource.edit}
                    show={resource.show}
                    options={resource.options}
                />
            ))}
        </AdminUI>
    );
});

const appQueryClient = new QueryClient({
    defaultOptions: {
        queries: {retry: false},
    },
});

const AppContent = observer(() => {
    const [themeName] = useStore<ThemeName>('themeName', 'soft');
    const singleTheme = themes.find(theme => theme.name === themeName)?.single;
    const lightTheme = themes.find(theme => theme.name === themeName)?.light;
    const darkTheme = themes.find(theme => theme.name === themeName)?.dark;
    const wrappedAuthProvider = useMemo(
        () => withAuthQueryClient(authProvider, appQueryClient),
        [],
    );

    return (
        <AdminContext
            store={store}
            queryClient={appQueryClient}
            authProvider={wrappedAuthProvider}
            i18nProvider={i18nProvider}
            theme={singleTheme}
            lightTheme={lightTheme}
            darkTheme={darkTheme}
            defaultTheme="light"
        >
            <ForcePasswordChangeDialog />
            <SessionExpiredDialog />
            <ContainerMemoryWarningDialog />
            <DynamicAdminUI />
        </AdminContext>
    );
});

const App = () => {
    // 初始化用户存储
    useEffect(() => {
        if (!userStore.isInitialized) {
            userStore.initialize().catch(error => {
                console.error('Failed to initialize user store:', error);
            });
        }
    }, []);

    return (
        <StoreContextProvider value={store}>
            <BrowserRouter>
                <AppContent />
            </BrowserRouter>
        </StoreContextProvider>
    );
};

export default App;
